import { Node, mergeAttributes, SingleCommands } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    image: {
      setImage: (options: { src: string; alt?: string; title?: string; width?: number }) => ReturnType;
    };
  }
}

const ResizableImageComponent: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  selected,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [resizing, setResizing] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const { src, alt, title, width } = node.attrs as {
    src: string;
    alt?: string;
    title?: string;
    width?: number;
  };

  const onMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setResizing(true);
      startX.current = event.clientX;
      startWidth.current = containerRef.current?.offsetWidth || 0;
    },
    [],
  );

  useEffect(() => {
    if (!resizing) return;

    const onMouseMove = (event: MouseEvent) => {
      const diff = event.clientX - startX.current;
      const newWidth = Math.max(100, startWidth.current + diff);
      if (containerRef.current) {
        containerRef.current.style.width = `${newWidth}px`;
      }
    };

    const onMouseUp = (event: MouseEvent) => {
      setResizing(false);
      const diff = event.clientX - startX.current;
      const newWidth = Math.max(100, startWidth.current + diff);
      updateAttributes({ width: Math.round(newWidth) });
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [resizing, updateAttributes]);

  return (
    <NodeViewWrapper as="span" className="resizable-image-wrapper" style={{ display: 'inline-block' }}>
      <div
        ref={containerRef}
        className={`resizable-image-container ${selected ? 'selected' : ''}`}
        style={{ width: width ? `${width}px` : undefined, display: 'inline-block', position: 'relative' }}
      >
        <img
          src={src}
          alt={alt || ''}
          title={title || ''}
          draggable={false}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
        {selected && (
          <>
            <div className="resize-handle resize-handle-right" onMouseDown={onMouseDown} />
            <div className="resize-handle resize-handle-bottom-right" onMouseDown={onMouseDown} />
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

export const ResizableImage = Node.create({
  name: 'image',

  group: 'inline',
  inline: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      width: {
        default: null,
        parseHTML: (element) => {
          // Check inline style first
          const styleWidth = element.style.width;
          if (styleWidth) {
            const parsed = parseFloat(styleWidth);
            if (!isNaN(parsed)) return parsed;
          }
          // Then check the width attribute
          const attrWidth = element.getAttribute('width');
          if (attrWidth) {
            const parsed = parseFloat(attrWidth);
            if (!isNaN(parsed)) return parsed;
          }
          return null;
        },
        renderHTML: (attributes) => {
          if (!attributes.width) return {};
          return { style: `width: ${attributes.width}px` };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'img[src]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageComponent);
  },

  addCommands() {
    return {
      setImage:
        (options: { src: string; alt?: string; title?: string; width?: number }) =>
        ({ commands }: { commands: SingleCommands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },
});
